import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import { createTaskTemplateApi } from "../api/taskTemplateApi";
import { templateStepCount, taskToTemplatePayload } from "../utils/taskTemplates";
import TaskChecklist from "./TaskChecklist";

const EMPTY = { name: "", title: "", notes: "", priority: "medium", checklist: [] };

const TaskTemplateLibrary = ({ isOpen, onClose, apiUrl, getToken, onUse }) => {
    const api = createTaskTemplateApi({ apiUrl, getToken });
    const [templates, setTemplates] = useState([]);
    const [draft, setDraft] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            setTemplates(await api.fetchTemplates());
        } catch (error) {
            toast.error(error.message);
        }
    };

    useEffect(() => {
        if (isOpen) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const save = async () => {
        if (!draft.name.trim()) return toast.error("Template name is required");
        setSaving(true);
        try {
            await api.createTemplate(
                taskToTemplatePayload(
                    {
                        title: draft.title || draft.name,
                        notes: draft.notes,
                        priority: draft.priority,
                        checklist: draft.checklist,
                    },
                    draft.name
                )
            );
            setDraft(EMPTY);
            toast.success("Template saved");
            await load();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.deleteTemplate(id);
            toast.success("Template deleted");
            await load();
        } catch (error) {
            toast.error(error.message);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="task-template-library"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ y: 24, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 24, opacity: 0 }}
                        className="task-template-library__panel"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <header className="task-template-library__head">
                            <h2>Task templates</h2>
                            <button type="button" onClick={onClose} aria-label="Close">
                                <X size={18} />
                            </button>
                        </header>

                        <p className="task-template-library__hint">
                            Save an SOP once, then spawn it on Create. Checklist steps stay
                            required and photo flags are kept; ticks and photos reset.
                        </p>

                        <ul className="task-template-library__list">
                            {templates.length === 0 && (
                                <li className="task-template-library__empty">No templates yet.</li>
                            )}
                            {templates.map((row) => (
                                <li key={row.id} className="task-template-library__item">
                                    <div>
                                        <strong>{row.name}</strong>
                                        <span>
                                            {templateStepCount(row)} step
                                            {templateStepCount(row) === 1 ? "" : "s"}
                                        </span>
                                    </div>
                                    <div className="task-template-library__item-actions">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onUse?.(row);
                                                onClose();
                                            }}
                                        >
                                            Use
                                        </button>
                                        <button
                                            type="button"
                                            aria-label="Delete template"
                                            onClick={() => remove(row.id)}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="task-template-library__form">
                            <h3>New template</h3>
                            <input
                                type="text"
                                placeholder="Template name (e.g. Opening shift)"
                                value={draft.name}
                                onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                            />
                            <input
                                type="text"
                                placeholder="Task title"
                                value={draft.title}
                                onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))}
                            />
                            <textarea
                                rows={2}
                                placeholder="Notes"
                                value={draft.notes}
                                onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
                            />
                            <TaskChecklist
                                items={draft.checklist}
                                onChange={(checklist) => setDraft((p) => ({ ...p, checklist }))}
                                canEdit
                                canToggle={false}
                            />
                            <button
                                type="button"
                                className="task-template-library__save"
                                onClick={save}
                                disabled={saving}
                            >
                                <Plus size={14} />
                                Save template
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default TaskTemplateLibrary;
